import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";
import { BrandPicker, brandLabel, useBrandNames } from "./brands";
import {
  DataTable,
  DateField,
  FilterSelect,
  PAGE_SIZE,
  PageHeader,
  ServerPagination,
  StatusChip,
  errorMessage,
  fmtDate,
  fmtPeriod,
  gbp,
  humanize,
  useCloStyles,
} from "./shared";

const ARREARS_STATUSES = ["open", "pending_approval", "holding", "sprive_funded", "cancelled", "paid"];

const OUTCOMES = [
  { value: "holding", label: "Hold (review later)" },
  { value: "sprive_funded", label: "Sprive funds the rewards" },
  { value: "cancelled", label: "Cancel the rewards" },
];

const COLUMNS = [
  { label: "Brand" },
  { label: "Period" },
  { label: "Outstanding", numeric: true },
  { label: "Written off", numeric: true },
  { label: "Recovered", numeric: true },
  { label: "Lines unpaid", numeric: true },
  { label: "Customers", numeric: true },
  { label: "Status" },
  { label: "Proposed outcome" },
  { label: "Review on" },
  { label: "Decided by" },
  { label: "Approved by" },
  { label: "Actions" },
];

type Filters = { brandId: string; status: string };
type Decision = { outcome: string; reason: string; reviewOn: string };

const EMPTY_FILTERS: Filters = { brandId: "", status: "" };
const EMPTY_DECISION: Decision = { outcome: "holding", reason: "", reviewOn: "" };

const effectOf = (c: any): string => {
  const amount = gbp(c.outstanding_minor);
  const lines = `${c.lines_unpaid} customer reward${Number(c.lines_unpaid) === 1 ? "" : "s"}`;
  if (c.proposed_outcome === "cancelled") return `Cancelling writes off ${amount} and cancels ${lines}.`;
  if (c.proposed_outcome === "sprive_funded") {
    return `Sprive funding writes off ${amount} from the retailer and settles ${lines} at Sprive's cost.`;
  }
  if (c.proposed_outcome === "holding") {
    return `Holding keeps ${amount} outstanding until ${fmtDate(c.review_on)}. No money moves.`;
  }
  return `Applies the proposed outcome "${humanize(c.proposed_outcome)}".`;
};

const Arrears = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [deciding, setDeciding] = useState<any>(null);
  const [decision, setDecision] = useState<Decision>(EMPTY_DECISION);
  const [busyId, setBusyId] = useState<number | null>(null);
  const names = useBrandNames(cases.map((c) => c.brand_id));

  // Overrides exist because setState has not applied yet when Clear re-fetches.
  const fetchCases = async (nextPage = 1, overrides?: Filters) => {
    const active = overrides || filters;
    const params: any = { page: nextPage, pageSize: PAGE_SIZE };
    if (active.brandId) params.brand_id = active.brandId;
    if (active.status) params.status = active.status;
    try {
      setLoading(true);
      const res: any = await axios.get(ENDPOINTS.CLO_ARREARS, getConfigGet(params));
      setCases(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setPage(nextPage);
    } catch (err) {
      notify(errorMessage(err, "Failed to load arrears cases"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => {
    setFilters(EMPTY_FILTERS);
    fetchCases(1, EMPTY_FILTERS);
  };

  const patch = async (c: any, body: any, done: string) => {
    try {
      setBusyId(c.id);
      await axios.patch(`${ENDPOINTS.CLO_ARREARS}/${c.id}`, body, { headers: getConfigPatch() });
      notify(done, "info");
      fetchCases(page);
      return true;
    } catch (err) {
      notify(errorMessage(err, "Failed to update arrears case"), "warning");
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const openDecide = (c: any) => {
    setDecision({
      outcome: c.proposed_outcome || EMPTY_DECISION.outcome,
      reason: "",
      reviewOn: c.review_on ? String(c.review_on).slice(0, 10) : "",
    });
    setDeciding(c);
  };

  const propose = async () => {
    const reason = decision.reason.trim();
    if (!reason || reason.length > 500) {
      notify("Reason must be 1–500 characters", "warning");
      return;
    }
    if (decision.outcome === "holding" && !decision.reviewOn) {
      notify("Pick a review date for a hold", "warning");
      return;
    }
    const body: any = { action: "propose", outcome: decision.outcome, reason };
    if (decision.outcome === "holding") body.reviewOn = decision.reviewOn;
    const ok = await patch(deciding, body, "Decision proposed. A second admin must approve it.");
    if (ok) setDeciding(null);
  };

  const approve = (c: any) => {
    if (!window.confirm(`Approve for ${brandLabel(names, c.brand_id)}, ${fmtPeriod(c.period)}?\n\n${effectOf(c)}`)) return;
    patch(c, { action: "approve" }, "Decision approved and applied");
  };

  const reject = (c: any) => {
    if (!window.confirm(`Reject the proposed "${humanize(c.proposed_outcome)}" for ${brandLabel(names, c.brand_id)}, ${fmtPeriod(c.period)}? The case goes back to open.`)) return;
    patch(c, { action: "reject" }, "Proposal rejected; case reopened");
  };

  const actionsFor = (c: any) => {
    const busy = busyId === c.id;
    if (c.status === "open" || c.status === "holding") {
      return (
        <Button size="small" variant="outlined" color="primary" disabled={busy} onClick={() => openDecide(c)}>
          Decide
        </Button>
      );
    }
    if (c.status === "pending_approval") {
      return (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" variant="contained" color="primary" disableElevation disabled={busy} onClick={() => approve(c)}>
            Approve
          </Button>
          <Button size="small" variant="outlined" disabled={busy} onClick={() => reject(c)}>
            Reject
          </Button>
        </div>
      );
    }
    return "—";
  };

  const filtered = !!(filters.brandId || filters.status);

  return (
    <div className={classes.main}>
      <PageHeader
        title="Arrears"
        subtitle="Unpaid retailer invoices 90+ days overdue. Each decision needs a second admin to approve it."
      />

      <div className={classes.filterContainer}>
        <BrandPicker
          className={classes.filterField}
          value={filters.brandId}
          onChange={(brandId) => setFilters({ ...filters, brandId })}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => setFilters({ ...filters, status })}
          options={ARREARS_STATUSES.map((s) => ({ value: s, label: humanize(s) }))}
        />
        <Button className={classes.filterButton} variant="contained" color="primary" disableElevation onClick={() => fetchCases(1)} disabled={loading}>
          Search
        </Button>
        <Button className={classes.filterButton} variant="outlined" onClick={clear}>Clear</Button>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <DataTable
          columns={COLUMNS}
          isEmpty={cases.length === 0}
          empty={
            filtered
              ? "No arrears cases match these filters."
              : "No arrears cases. A case opens automatically when an invoice is 90 days overdue."
          }
          footer={<ServerPagination page={page} total={total} onChange={fetchCases} />}
        >
          {cases.map((c) => (
            <TableRow key={c.id} hover>
              <TableCell>{brandLabel(names, c.brand_id)}</TableCell>
              <TableCell>{fmtPeriod(c.period)}</TableCell>
              <TableCell align="right"><strong>{gbp(c.outstanding_minor)}</strong></TableCell>
              <TableCell align="right">{gbp(c.written_off_minor)}</TableCell>
              <TableCell align="right">{gbp(c.recovered_minor)}</TableCell>
              <TableCell align="right">{c.lines_unpaid}</TableCell>
              <TableCell align="right">{c.customers_affected}</TableCell>
              <TableCell><StatusChip status={c.status} /></TableCell>
              <TableCell>
                {c.proposed_outcome ? humanize(c.proposed_outcome) : "—"}
                {c.reason && (
                  <Typography variant="caption" display="block" className={classes.muted}>{c.reason}</Typography>
                )}
              </TableCell>
              <TableCell>{fmtDate(c.review_on)}</TableCell>
              <TableCell>{c.decided_by || "—"}</TableCell>
              <TableCell>{c.approved_by || "—"}</TableCell>
              <TableCell>{actionsFor(c)}</TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}

      <Dialog open={!!deciding} onClose={() => busyId == null && setDeciding(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Decide arrears case</DialogTitle>
        <DialogContent>
          {deciding && (
            <div className={classes.dialogContent}>
              <Typography variant="body2" className={classes.muted}>
                {brandLabel(names, deciding.brand_id)} · {fmtPeriod(deciding.period)} · {gbp(deciding.outstanding_minor)} outstanding
                across {deciding.lines_unpaid} rewards. A different admin must approve this before it takes effect.
              </Typography>
              <FilterSelect
                label="Outcome"
                value={decision.outcome}
                onChange={(outcome) => setDecision({ ...decision, outcome })}
                options={OUTCOMES}
                allLabel={null}
                fullWidth
              />
              {decision.outcome === "holding" && (
                <DateField
                  label="Review on"
                  value={decision.reviewOn}
                  onChange={(reviewOn) => setDecision({ ...decision, reviewOn })}
                  required
                  fullWidth
                />
              )}
              <TextField
                variant="outlined"
                size="small"
                label="Reason"
                value={decision.reason}
                onChange={(e) => setDecision({ ...decision, reason: e.target.value })}
                multiline
                rows={3}
                inputProps={{ maxLength: 500 }}
                helperText={`${decision.reason.length}/500`}
                required
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeciding(null)} disabled={busyId != null}>Cancel</Button>
          <Button onClick={propose} color="primary" variant="contained" disableElevation disabled={busyId != null}>
            {busyId != null ? <CircularProgress size={20} /> : "Propose"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default Arrears;
